import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTheme } from '../../context/ThemeContext';
import type { ColorTheme } from '../../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../utils/theme';

export type UpsellPolicyValue = 'none' | 'purchasable' | 'included';
export type UpsellPolicy = Record<string, UpsellPolicyValue>;
export interface UpsellCatalogItem { id: string; name: string; priceCents: number; isActive?: boolean }

const CHOICES: { value: UpsellPolicyValue; label: string }[] = [
  { value: 'none', label: 'Not offered' },
  { value: 'purchasable', label: 'Purchasable' },
  { value: 'included', label: 'Included' },
];

/**
 * 1.4 R05: per-upsell choice for a membership plan or bundle. Missing key = purchasable.
 */
export default function UpsellPolicyPicker({ catalog, value, onChange, title = 'Add-ons', hint }: {
  catalog: UpsellCatalogItem[];
  value: UpsellPolicy;
  onChange: (next: UpsellPolicy) => void;
  title?: string;
  hint?: string;
}) {
  const { colors } = useTheme();
  const styles = makeStyles(colors);
  if (catalog.length === 0) return null;
  return (
    <View style={styles.wrap}>
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.hint}>{hint ?? 'Included = free with every wash on this plan. Purchasable = offered at the regular price. Not offered = hidden.'}</Text>
      {catalog.map(u => {
        const cur = value[u.id] ?? 'purchasable';
        return (
          <View key={u.id} style={styles.row}>
            <Text style={styles.name} numberOfLines={1}>{u.name} · ${(u.priceCents / 100).toFixed(2)}</Text>
            <View style={styles.chips}>
              {CHOICES.map(c => (
                <TouchableOpacity key={c.value} onPress={() => onChange({ ...value, [u.id]: c.value })}
                  style={[styles.chip, cur === c.value && styles.chipOn]}
                  accessibilityRole="radio" accessibilityState={{ selected: cur === c.value }} accessibilityLabel={`${u.name}: ${c.label}`}>
                  <Text style={[styles.chipText, cur === c.value && styles.chipTextOn]}>{c.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        );
      })}
    </View>
  );
}

const makeStyles = (c: ColorTheme) => StyleSheet.create({
  wrap: { marginTop: spacing.sm, marginBottom: spacing.md },
  title: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: 2 },
  hint: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.xs },
  row: { paddingVertical: 6, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: c.borderLight },
  name: { fontSize: fontSize.sm, color: c.text, marginBottom: 4 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: borderRadius.full ?? 14, borderWidth: 1, borderColor: c.border, backgroundColor: c.surface },
  chipOn: { backgroundColor: c.primary, borderColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.text },
  chipTextOn: { color: '#fff', fontWeight: fontWeight.semibold },
});
